import { useReactiveVar } from '@apollo/client';
import { Button, CheckboxItem } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import { useGetParkingZonesWithBuyablePermits } from 'hooks/useGetParkingZonesWithBuyablePermits';
import { useGetParkingZonesWithPrepaidTickets } from 'hooks/useGetParkingZonesWithPrepaidTickets';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { featureCountryCode } from 'states/persistInStorage';
import { useMapStore } from 'stores/mapStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { useSearchStore } from 'stores/searchStore';
import { Divider } from 'styles/CommonLayoutStyles';
import { ColumnWithMargins, StyledWrapper } from 'styles/ContainerStyles';
import { ParkZone } from 'types/generatedSchemaTypes';
import { mapFilter } from 'utils/parkUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { useUiStore } from 'stores/uiStore';

export type MapFilterType =
  | 'parkingAvailable'
  | 'chargingAvailable'
  | 'permits'
  | 'prepaidsAvailable';

export interface FiltersState {
  parkingAvailable: boolean;
  chargingAvailable: boolean;
  permits: boolean;
  prepaidsAvailable: boolean;
}

const DEFAULT_FILTERS: FiltersState = {
  parkingAvailable: false,
  chargingAvailable: false,
  permits: false,
  prepaidsAvailable: false,
};

const ZoneFilterModal: React.FC = () => {
  const { t } = useTranslation();
  const currentFilters = useMapStore((state) => state.mapFilters);
  const appCountry = useReactiveVar(featureCountryCode);
  const { setFilterParkzoneData, filterParkzoneData, parkZonesData } =
    useParkzoneStore();
  const { resetSearch } = useSearchStore((state) => state);
  const isFilterLoading = useMapStore((state) => state.parkZonesFilterLoading);
  const activeFilterCount = Object.values(currentFilters).filter(
    (value) => value === true
  ).length;
  const [filters, setFilters] = useState({
    ...DEFAULT_FILTERS,
    ...currentFilters,
  });
  const isPermitsFilterActive = Boolean(filters.permits);
  const isPrepaidsFilterActive = Boolean(filters.prepaidsAvailable);
  const { parkingZonesData: ltpZones, loading: ltpLoading } =
    useGetParkingZonesWithBuyablePermits(appCountry, isPermitsFilterActive);

  const { parkingZonesDataUids, loading: prepaidLoading } =
    useGetParkingZonesWithPrepaidTickets(appCountry, isPrepaidsFilterActive);

  useEffect(() => {
    useMapStore.getState().setMapFilters(filters);
  }, [filters]);

  const handleConfirmCancel = async () => {
    useMapStore.getState().setMapFilters(DEFAULT_FILTERS);
    useUiStore.getState().setSheetModal('');
  };
  const handleConfirmFilter = async () => {
    useMapStore.getState().setMapFilters(filters);
    resetSearch();
    useUiStore.getState().setSheetModal('');
  };

  const handleCheckboxChange = (key: MapFilterType) => {
    setFilters((prev) => {
      const updatedFilters = { ...prev, [key]: !prev[key] };
      return updatedFilters;
    });
  };

  const checkboxOptions = [
    { key: 'parkingAvailable', label: t('search.zoneFilter.parkingAvailable') },
    {
      key: 'chargingAvailable',
      label: t('search.zoneFilter.chargingAvailable'),
    },
    { key: 'permits', label: t('search.zoneFilter.permitsAvailable') },
    {
      key: 'prepaidsAvailable',
      label: t('search.zoneFilter.prepaidsAvailable'),
    },
  ];

  const buildFilterData = useCallback(
    ({
      permitZones,
      prepaidZoneUids,
    }: {
      permitZones?: ParkZone[];
      prepaidZoneUids?: string[];
    }) => {
      let zones = parkZonesData;

      if (filters.permits && permitZones) {
        zones = permitZones;
      }
      if (filters.prepaidsAvailable && prepaidZoneUids) {
        zones = zones.filter(
          (zone) => zone?.uid && prepaidZoneUids.includes(zone.uid)
        );
      }
      return zones.filter((zone) => mapFilter(zone, filters));
    },
    [filters, parkZonesData]
  );

  useEffect(() => {
    if (parkZonesData.length > 0) {
      const finalData = buildFilterData({
        permitZones: ltpZones,
        prepaidZoneUids: parkingZonesDataUids,
      });

      setFilterParkzoneData(finalData);
    }
  }, [
    filters,
    ltpZones,
    parkingZonesDataUids,
    parkZonesData,
    buildFilterData,
    setFilterParkzoneData,
  ]);

  const getButtonText = (): string => {
    if (activeFilterCount > 0) {
      if (
        (ltpZones.length === 0 && ltpLoading) ||
        isFilterLoading ||
        prepaidLoading
      ) {
        return t('button.filtering');
      }
      return t('button.filter', { number: filterParkzoneData.length });
    }

    return t('button.showAllLocations');
  };

  return (
    <StyledWrapper mt={30}>
      <View>
        {checkboxOptions.map(({ key, label }, index) => (
          <>
            <CheckboxItem
              key={key}
              testID={`filter-button-${key}`}
              keyName={key as MapFilterType}
              label={label}
              isChecked={filters[key as keyof typeof filters]}
              onToggle={handleCheckboxChange}
              checkedIcon={<Icon name="Checkbox-Checked-Square" />}
            />
            {index !== checkboxOptions.length - 1 && <Divider />}
          </>
        ))}
      </View>
      <ColumnWithMargins mb={10} mt={20}>
        <Button
          {...testIdentifiers('filter-confirm-button')}
          testID="filter-confirm-button"
          text={getButtonText()}
          size="l"
          buttonType={'secondary'}
          onPress={handleConfirmFilter}
        />
      </ColumnWithMargins>
      <Button
        {...testIdentifiers('filter-cancel-button')}
        testID="filter-cancel-button"
        text={t('button.cancel')}
        size="l"
        buttonType="tertiary"
        onPress={handleConfirmCancel}
      />
    </StyledWrapper>
  );
};

export default ZoneFilterModal;
